// external dependencies
// charting components
import {
	BarElement,
	CategoryScale,
	ChartData,
	Chart as ChartJS,
	ChartOptions,
	LinearScale,
	Title,
	Tooltip,
} from "chart.js";
import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";

import { Spinner } from "@performyard/py-components";

import EmptyChart from "../../../../../../components/EmptyChart";
// components
import { averageCohortFactors, formatTopCohortLabel } from "./TopCohortsUtils";

// store
import useStore from "../../../../store";

// api
import { useCohortFactorSummary, useCohorts } from "../../../../../../api/hooks";

// utils
import { getColor } from "../../../../../../utils/getColor";

// styles
import css from "./TopCohorts.module.scss";

// constants
const NUMBER_COHORTS = 5;

// register chartjs plugins
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface BarChartOptions extends ChartOptions<"bar"> {
	barThickness: number;
	barBorderRadius: number;
}

// define plugin for chart area right border
// https://www.chartjs.org/docs/3.7.0/samples/plugins/chart-area-border.html
const chartAreaRightBorder = {
	id: "chartAreaBorder",
	beforeDraw(chart) {
		const {
			ctx,
			chartArea: { left, top, width, height },
		} = chart;
		ctx.save();
		ctx.strokeStyle = getColor("neutral", 1);
		ctx.lineWidth = 1;

		// draw the right border
		ctx.beginPath();
		ctx.moveTo(left + width, top);
		ctx.lineTo(left + width, top + height);
		ctx.stroke();

		ctx.restore();
	},
};

/**
 * React component to render a chart of the top NUMBER_COHORTS cohorts
 * for the date range, using a bar chart
 */
const TopCohortsChart = () => {
	// setup
	const [filter] = useStore((state) => [state.filter]);
	// api response for cohorts metadata
	const { data: cohortsMetadata, isLoading: cohortsMetadataLoading } = useCohorts();

	// api response for the date range
	const { data: cohortsData, isLoading } = useCohortFactorSummary({
		startDate: filter?.startDate ?? null,
		endDate: filter?.endDate ?? null,
	});

	// get the top NUMBER_COHORTS cohorts
	const topCohorts = useMemo(() => {
		if (cohortsData && !cohortsMetadataLoading) {
			// average each cohort's factor rows, then sort by average
			const averages = averageCohortFactors(cohortsData.factorsData).sort((a, b) =>
				a.average > b.average || cohortsMetadata?.[a.cohort]?.name > cohortsMetadata?.[b.cohort]?.name ? -1 : 1
			);

			// then return the top X cohorts that actually exist
			return (
				averages
					// only show cohorts that are found in useCohorts response (active)
					// TODO - this is a hack, we should be only get the active cohorts from the useCohortsSummary response
					.filter((coh) => cohortsMetadata?.cohorts.find((coho) => coho._id === coh.cohort))
					.slice(0, NUMBER_COHORTS)
			);
		}
		return [];
	}, [cohortsData, cohortsMetadataLoading]);

	// chart data
	const data: ChartData<"bar"> = {
		// no cohorts are found, displays 5 generic cohorts
		// to match design where text is superimposed on chart
		labels: topCohorts.length
			? topCohorts.map((cohort, index) =>
					formatTopCohortLabel(
						cohortsMetadata?.cohorts.find((coho) => coho._id === cohort.cohort)?.name ||
							`Cohort ${(index + 1).toString()} (Unknown)`
					)
				)
			: ["Cohort 1", "Cohort 2", "Cohort 3", "Cohort 4", "Cohort 5"],
		datasets: [
			{
				label: "Top Cohorts",
				data: topCohorts.map((cohort) => cohort.average),
				backgroundColor: getColor("primary", 5),
				borderWidth: 0,
			},
		],
	};

	// chart options
	const options: BarChartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		barThickness: 8,
		barBorderRadius: 2,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				displayColors: false,
				backgroundColor: getColor("neutral", 7),
				titleFont: {
					weight: "normal",
					family: "Source Sans Pro, sans-serif",
					size: 12,
				},
				bodyFont: {
					family: "Source Sans Pro, sans-serif",
					size: 12,
				},
				caretPadding: 8,
				cornerRadius: 4,
				callbacks: {
					// format tooltip title from metadata or label
					//truncate over 30 characters
					title: (context) => {
						const cohortId = topCohorts[context[0].dataIndex].cohort;
						let tooltipTitle =
							cohortsMetadata?.cohorts.find((cohort) => cohort._id === cohortId)?.name ||
							topCohorts[context[0].dataIndex].cohort;

						if (tooltipTitle.length > 40) {
							tooltipTitle = tooltipTitle.slice(0, 37) + "...";
						}

						return tooltipTitle;
					},
					// format non 0 numeric value to 1 decimal place, or default to formatted value
					label: (context) => {
						if (context.raw === 0) {
							return "0";
						} else if (typeof context.raw === "number") {
							return context.raw.toFixed(1);
						} else {
							return context.formattedValue;
						}
					},
				},
			},
		},
		scales: {
			x: {
				border: {
					color: getColor("neutral", 1),
					width: 1,
				},
				grid: {
					color: getColor("neutral", 1),
					drawTicks: false,
				},
				ticks: {
					padding: 8,
					maxRotation: 0,
					minRotation: 0,
					color: getColor("primary", 9),
					font: {
						size: 10,
						family: "Source Sans Pro, sans-serif",
					},
				},
			},
			y: {
				display: true,
				min: 0,
				suggestedMax: 5,
				border: {
					color: getColor("neutral", 1),
					width: 1,
				},
				grid: {
					color: getColor("neutral", 1),
					drawTicks: false,
				},
				ticks: {
					padding: 8,
					stepSize: 1,
					color: getColor("primary", 9),
					font: {
						size: 10,
						family: "Source Sans Pro, sans-serif",
					},
				},
			},
		},
	};

	if (isLoading) {
		return (
			<div className={css.spinnerBox}>
				<Spinner size={20} />
			</div>
		);
	}

	return (
		<>
			{topCohorts.length === 0 ? (
				<EmptyChart type="topCohort" />
			) : (
				<Bar data={data} options={options} plugins={[chartAreaRightBorder]} />
			)}
		</>
	);
};

export default TopCohortsChart;
